import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import { Search, ChevronRight, Snowflake, Thermometer, Factory, Zap, ArrowRight, Wrench } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const categoryIcons = {
  'water-cooled-brine': Snowflake,
  'water-cooled-chilled-water': Thermometer,
  'ammonia': Factory,
  'ultra-low-temp': Zap,
  'custom': Wrench,
};

const categoryDetails = {
  'water-cooled-brine': {
    hero: 'Water Cooled Brine Chillers',
    tagline: 'Custom-Engineered Low Temperature Cooling Solutions',
    description: 'Our Water Cooled Brine Chillers are custom-designed for demanding low-temperature industrial applications. With over 35+ years of engineering expertise, we manufacture tailor-made systems based strictly on client process requirements.\n\nDesigned for applications such as pharmaceuticals, chemical processing, food & beverage industries, and specialized industrial cooling, our brine chillers deliver precise and stable temperature control using glycol, methanol, calcium chloride, or other secondary refrigerants.\n\nEach system is engineered considering required capacity, temperature range, pressure conditions, and site environment to ensure long-term reliability and energy efficiency.\n\nWe do not offer standard mass-produced models — every system is built to specification.',
    features: [
      'Custom-built capacity as per client requirement',
      'Brine outlet temperatures from +5°C to -40°C (extendable based on application)',
      'SS304 / SS316L evaporator options for corrosive brine applications',
      'Screw compressor or multi-compressor configurations',
      'VFD integration for energy optimization',
      'Advanced control systems with IoT / BMS compatibility',
      'Designed for high efficiency and stable low-temperature operation',
      'Suitable for glycol, methanol, and calcium chloride brine systems',
    ],
    capabilities: [
      'Multiple screw compressor configurations',
      'High-pressure ratio design capability',
      'Energy-efficient system architecture',
      'Site-specific condenser options (Shell & Tube / Evaporative)',
      'Pharma-compliant and process-critical design standards',
    ],
    configProcess: [
      'Required Cooling Capacity',
      'Required Temperature Range',
      'Outlet Temperature Requirement',
      'Condenser Type Preference',
      'Evaporator Material Selection',
      'Preferred Compressor Brand',
      'Installation Location',
      'IoT / BMS Requirements',
      'Any Special Technical Conditions',
    ],
  },
  'water-cooled-chilled-water': {
    hero: 'Water Cooled Chillers (Chilled Water)',
    tagline: 'High-efficiency chilled water systems from +5°C to +15°C',
    description: 'Our water-cooled chilled water systems are designed for HVAC comfort cooling and process cooling applications. Using R-134a or R-513A, these chillers deliver industry-leading COP values with flooded or DX evaporators. Ideal for data centers, commercial buildings, manufacturing plants, and district cooling applications. Twin-screw compressors from Bitzer and Hanbell ensure reliable operation with excellent part-load performance.',
    features: ['High COP (up to 5.5+ at ARI conditions)', 'Flooded evaporator option for superior heat transfer', 'Dual-compressor staging for N+1 redundancy', 'Full BMS integration (Modbus, BACnet, OPC-UA)', 'AHRI 550/590 certified performance', 'Low-GWP refrigerant options (R-513A, R-1234ze)'],
  },
  'ammonia': {
    hero: 'Ammonia Chillers',
    tagline: 'Natural refrigerant R-717 for industrial-scale cooling',
    description: 'Ammonia (R-717) chillers from Laminate Chillers offer zero ODP and zero GWP cooling for heavy-duty industrial applications. Engineered for cold storage, dairy, brewery, ice plants, and chemical processing, our ammonia systems deliver exceptional thermodynamic efficiency. Built with ASME U-stamp pressure vessels and full compliance with IIAR and EN 378 safety standards.',
    features: ['Zero ODP, zero GWP — the most sustainable refrigerant', 'Operating range: +10°C to -45°C', 'Open-drive and semi-hermetic compressor options', 'ASME U-stamp pressure vessels', 'Full ammonia safety system (detection, ventilation, emergency relief)', 'IIAR and EN 378 compliant design'],
  },
  'ultra-low-temp': {
    hero: 'Ultra Low Temperature Chillers',
    tagline: 'Cascade refrigeration systems for -40°C to -60°C',
    description: 'Our ultra-low temperature chillers use cascade refrigeration technology to achieve temperatures between -40°C and -60°C. These specialized systems combine a high-temperature stage (R-507A or R-404A) with a low-temperature stage (R-23 or R-508B) through an intermediate heat exchanger. Designed for pharmaceutical freeze-drying, chemical reactor cooling, cryogenic processing, and specialized cold chain applications.',
    features: ['Cascade system: HT + LT screw compressors', 'Operating range: -40°C to -60°C', 'PLC-controlled staging with full safety interlocks', 'ATEX-rated options for hazardous environments', 'SS316L wetted path throughout', 'Customizable for specific process requirements'],
  },
  'custom': {
    hero: 'Custom Tailor-Made Chillers',
    tagline: 'Engineered solutions for your unique process requirements',
    description: 'Every industrial process is unique. At Laminate Chillers, we specialize in designing and manufacturing custom chiller solutions tailored to your exact specifications. Whether you need non-standard materials, specific certifications, unusual temperature ranges, or integration with existing systems — our engineering team will design a solution from scratch. Submit your requirements below and our team will provide a detailed proposal.',
    features: ['Custom capacity from 10 TR to 500+ TR', 'Any leaving temperature from +15°C to -60°C', 'Material selection: SS304, SS316L, Cu-Ni, Titanium, Hastelloy', 'Compliance: CE, ASME, PED, ATEX, GMP, FDA', 'Full IQ/OQ/PQ documentation for pharma', 'Integration with existing BMS/SCADA systems'],
  },
};

export default function Products() {
  const { category } = useParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [total, setTotal] = useState(0);

  useEffect(() => {
    const catDetail = category ? categoryDetails[category] : null;
    document.title = catDetail
      ? `${catDetail.hero} | Laminate Chillers – Hyderabad`
      : 'Products | Industrial Chillers – Laminate Chillers Hyderabad';
  }, [category]);

  useEffect(() => {
    axios.get(`${API_URL}/api/products/categories`).then(r => setCategories(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!category) { setLoading(false); return; }
    setLoading(true);
    const params = { limit: 20 };
    if (category && category !== 'custom') params.category = category;
    if (search) params.search = search;
    axios.get(`${API_URL}/api/products`, { params })
      .then(r => { setProducts(r.data.products || []); setTotal(r.data.total || 0); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [category, search]);

  const currentCategory = categories.find(c => c.slug === category);
  const catDetail = category ? categoryDetails[category] : null;

  // Category listing page (no category selected)
  if (!category) {
    return (
      <div className="page-transition">
        <section className="py-10 sm:py-14" style={{ background: 'var(--surface-2)' }}>
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
              <Link to="/" className="hover:text-[var(--brand-navy)]">Home</Link>
              <ChevronRight className="w-3.5 h-3.5" /><span>Products</span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight" style={{ fontFamily: 'Space Grotesk', color: 'var(--brand-navy)' }}>Our Products</h1>
            <p className="text-muted-foreground mt-1">Custom-engineered industrial chiller solutions for every application</p>
          </div>
        </section>

        <section className="py-10" style={{ background: 'var(--surface-0)' }}>
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {categories.map((cat) => {
                const Icon = categoryIcons[cat.slug] || Snowflake;
                return (
                  <Link to={`/products/${cat.slug}`} key={cat.slug} data-testid={`category-card-${cat.slug}`}>
                    <Card className="product-card-hover border cursor-pointer h-full" style={{ background: 'var(--surface-1)' }}>
                      <CardContent className="p-6">
                        <div className="w-11 h-11 rounded-lg flex items-center justify-center mb-4" style={{ background: 'rgba(14,138,138,0.1)' }}>
                          <Icon className="w-5 h-5" style={{ color: 'var(--brand-teal)' }} />
                        </div>
                        <h3 className="font-semibold text-base mb-2" style={{ color: 'var(--brand-navy)', fontFamily: 'Space Grotesk' }}>{cat.name}</h3>
                        <p className="text-sm text-muted-foreground mb-3 line-clamp-3">{cat.description}</p>
                        <div className="flex items-center gap-2 text-sm font-medium" style={{ color: 'var(--brand-teal)' }}>
                          {cat.slug === 'custom' ? 'Submit Requirements' : 'View Models'} <ArrowRight className="w-4 h-4" />
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-10" style={{ background: 'var(--brand-navy)' }}>
          <div className="max-w-6xl mx-auto px-4 sm:px-6 text-center">
            <h2 className="text-xl sm:text-2xl font-semibold tracking-tight text-white mb-3" style={{ fontFamily: 'Space Grotesk' }}>Need a Custom Solution?</h2>
            <p className="text-slate-400 mb-5 max-w-lg mx-auto">We design and manufacture chillers tailored to your exact process requirements. Submit your specifications and our engineering team will respond within 48 hours.</p>
            <Link to="/products/custom">
              <Button className="text-white px-6" style={{ background: 'var(--brand-teal)' }}>Submit Requirements <ArrowRight className="w-4 h-4 ml-2" /></Button>
            </Link>
          </div>
        </section>
      </div>
    );
  }

  // Category detail page
  return (
    <div className="page-transition">
      {/* Header */}
      <section className="py-10 sm:py-14" style={{ background: 'var(--surface-2)' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
            <Link to="/" className="hover:text-[var(--brand-navy)]">Home</Link>
            <ChevronRight className="w-3.5 h-3.5" />
            <Link to="/products" className="hover:text-[var(--brand-navy)]">Products</Link>
            <ChevronRight className="w-3.5 h-3.5" />
            <span>{catDetail?.hero || currentCategory?.name}</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight" style={{ fontFamily: 'Space Grotesk', color: 'var(--brand-navy)' }}>{catDetail?.hero || currentCategory?.name}</h1>
          <p className="text-muted-foreground mt-1 text-base">{catDetail?.tagline}</p>
        </div>
      </section>

      {/* Category description */}
      {catDetail && (
        <section className="py-8" style={{ background: 'var(--surface-0)' }}>
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h2 className="text-lg font-semibold tracking-tight mb-3" style={{ fontFamily: 'Space Grotesk', color: 'var(--brand-navy)' }}>Overview</h2>
                <div className="space-y-3">
                  {catDetail.description.split('\n\n').map((para, i) => (
                    <p key={i} className="text-sm text-muted-foreground leading-relaxed">{para}</p>
                  ))}
                </div>
              </div>
              <div>
                <h2 className="text-lg font-semibold tracking-tight mb-3" style={{ fontFamily: 'Space Grotesk', color: 'var(--brand-navy)' }}>Key Features</h2>
                <ul className="space-y-2">
                  {catDetail.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <span style={{ color: 'var(--brand-teal)' }} className="mt-1 shrink-0">&#10003;</span>
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Engineering Capabilities (if available) */}
      {catDetail?.capabilities && (
        <section className="py-8 border-t" style={{ background: 'var(--surface-1)' }}>
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <h2 className="text-lg font-semibold tracking-tight mb-4" style={{ fontFamily: 'Space Grotesk', color: 'var(--brand-navy)' }}>Engineering Capabilities</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {catDetail.capabilities.map((cap, i) => (
                <Card key={i} style={{ background: 'var(--surface-0)' }}>
                  <CardContent className="p-4 flex items-start gap-3">
                    <div className="w-7 h-7 rounded-full flex items-center justify-center shrink-0 mt-0.5" style={{ background: 'rgba(14,138,138,0.1)' }}>
                      <Zap className="w-3.5 h-3.5" style={{ color: 'var(--brand-teal)' }} />
                    </div>
                    <p className="text-sm text-muted-foreground">{cap}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Custom Configuration Process (if available) */}
      {catDetail?.configProcess && (
        <section className="py-8 border-t" style={{ background: 'var(--surface-0)' }}>
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <h2 className="text-lg font-semibold tracking-tight mb-2" style={{ fontFamily: 'Space Grotesk', color: 'var(--brand-navy)' }}>Custom Configuration Process</h2>
            <p className="text-sm text-muted-foreground mb-4">Instead of fixed models, clients can specify:</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {catDetail.configProcess.map((item, i) => (
                <div key={i} className="flex items-center gap-2 p-3 rounded-lg border" style={{ background: 'var(--surface-1)' }}>
                  <div className="w-5 h-5 rounded-full flex items-center justify-center shrink-0 text-[10px] font-bold text-white" style={{ background: 'var(--brand-teal)' }}>{i + 1}</div>
                  <span className="text-sm" style={{ color: 'var(--brand-navy)' }}>{item}</span>
                </div>
              ))}
            </div>
            <div className="mt-4 p-4 rounded-lg border-l-4" style={{ borderColor: 'var(--brand-teal)', background: 'rgba(14,138,138,0.04)' }}>
              <p className="text-sm font-medium" style={{ color: 'var(--brand-navy)' }}>Our engineering team evaluates the process and designs the optimal system configuration.</p>
            </div>
          </div>
        </section>
      )}

      {/* Enquiry Form */}
      <EnquiryForm productCategory={catDetail?.hero || currentCategory?.name || category} categorySlug={category} />
    </div>
  );
}

// ─── Enquiry Form Component ───
function EnquiryForm({ productCategory, categorySlug }) {
  const [form, setForm] = useState({
    product_category: productCategory,
    required_capacity: '',
    temperature_range: '',
    outlet_temperature: '',
    condenser_type: '',
    evaporator_tube_material: '',
    compressor_brand: '',
    compressor_brand_other: '',
    installation_location: '',
    iot_bms_required: 'no',
    special_requirements: '',
    additional_specs: '',
    company_name: '',
    contact_name: '',
    phone: '',
    email: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [enquiryId, setEnquiryId] = useState('');

  const update = (f, v) => setForm(p => ({ ...p, [f]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.company_name || !form.contact_name || !form.email) {
      const { toast } = await import('sonner');
      toast.error('Company name, contact name, and email are required');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/api/product-enquiry`, { ...form, product_category: productCategory });
      setEnquiryId(res.data.enquiry_id);
      setSubmitted(true);
    } catch (err) {
      const { toast } = await import('sonner');
      toast.error(err.response?.data?.detail || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <section className="py-12" style={{ background: 'var(--surface-2)' }}>
        <div className="max-w-2xl mx-auto px-4 sm:px-6 text-center">
          <div className="w-14 h-14 rounded-full mx-auto mb-5 flex items-center justify-center" style={{ background: 'rgba(14,138,138,0.1)' }}>
            <svg className="w-7 h-7" style={{ color: 'var(--brand-teal)' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 6L9 17l-5-5"/></svg>
          </div>
          <h2 className="text-xl sm:text-2xl font-semibold tracking-tight mb-2" style={{ fontFamily: 'Space Grotesk', color: 'var(--brand-navy)' }}>Enquiry Submitted Successfully</h2>
          <p className="text-muted-foreground mb-1">Enquiry Reference: <span className="font-mono font-semibold" style={{ color: 'var(--brand-teal)' }}>{enquiryId}</span></p>
          <p className="text-sm text-muted-foreground mb-6">Our engineering team will review your requirements and contact you within 24–48 hours.</p>
          <div className="flex gap-3 justify-center">
            <Link to="/products"><Button variant="outline">Browse More Products</Button></Link>
            <Link to="/"><Button className="text-white" style={{ background: 'var(--brand-teal)' }}>Back to Home</Button></Link>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-10" id="enquiry" style={{ background: 'var(--surface-2)' }}>
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-6">
          <h2 className="text-xl sm:text-2xl font-semibold tracking-tight" style={{ fontFamily: 'Space Grotesk', color: 'var(--brand-navy)' }}>Submit Your Requirements</h2>
          <p className="text-sm text-muted-foreground mt-1">Tell us your specific needs and we'll design a custom {productCategory} solution</p>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Technical Requirements */}
          <Card className="mb-5" style={{ background: 'var(--surface-1)' }}>
            <CardContent className="p-5">
              <h3 className="font-semibold text-sm mb-4 flex items-center gap-2" style={{ fontFamily: 'Space Grotesk', color: 'var(--brand-navy)' }}>
                <span className="w-6 h-6 rounded-full flex items-center justify-center text-xs text-white" style={{ background: 'var(--brand-teal)' }}>1</span>
                Technical Requirements
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Required Capacity</label>
                  <Input data-testid="enq-capacity" value={form.required_capacity} onChange={(e) => update('required_capacity', e.target.value)} placeholder="e.g., 200 kW or 60 TR" />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Required Temperature Range</label>
                  <Input data-testid="enq-temp-range" value={form.temperature_range} onChange={(e) => update('temperature_range', e.target.value)} placeholder="e.g., -15°C to -20°C" />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Chiller Outlet Temperature</label>
                  <Input data-testid="enq-outlet-temp" value={form.outlet_temperature} onChange={(e) => update('outlet_temperature', e.target.value)} placeholder="e.g., -15°C" />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Condenser Type</label>
                  <select className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm" data-testid="enq-condenser" value={form.condenser_type} onChange={(e) => update('condenser_type', e.target.value)}>
                    <option value="">Select...</option>
                    <option value="Shell & Tube">Shell & Tube</option>
                    <option value="Evaporative Condenser">Evaporative Condenser</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Evaporator Tubes Material</label>
                  <select className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm" data-testid="enq-tube-material" value={form.evaporator_tube_material} onChange={(e) => update('evaporator_tube_material', e.target.value)}>
                    <option value="">Select...</option>
                    <option value="MS">MS</option>
                    <option value="SS304">SS304</option>
                    <option value="SS316L">SS316L</option>
                    <option value="Copper">Copper</option>
                    <option value="Cu-Nickel">Cu-Nickel</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Preferred Compressor Brand</label>
                  <select className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm" data-testid="enq-compressor" value={form.compressor_brand} onChange={(e) => update('compressor_brand', e.target.value)}>
                    <option value="">Select...</option>
                    <option value="Bitzer">Bitzer</option>
                    <option value="J&E Hall">J&E Hall</option>
                    <option value="SRM">SRM</option>
                    <option value="Other">Any other (specify below)</option>
                  </select>
                </div>
                {form.compressor_brand === 'Other' && (
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">Specify Compressor Brand</label>
                    <Input value={form.compressor_brand_other} onChange={(e) => update('compressor_brand_other', e.target.value)} placeholder="Enter brand name" />
                  </div>
                )}
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Installation Location</label>
                  <Input data-testid="enq-location" value={form.installation_location} onChange={(e) => update('installation_location', e.target.value)} placeholder="City, Country" />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground">IoT / BMS Requirement</label>
                  <select className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm" data-testid="enq-iot" value={form.iot_bms_required} onChange={(e) => update('iot_bms_required', e.target.value)}>
                    <option value="no">No</option>
                    <option value="yes">Yes</option>
                  </select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Special Requirements */}
          <Card className="mb-5" style={{ background: 'var(--surface-1)' }}>
            <CardContent className="p-5">
              <h3 className="font-semibold text-sm mb-4 flex items-center gap-2" style={{ fontFamily: 'Space Grotesk', color: 'var(--brand-navy)' }}>
                <span className="w-6 h-6 rounded-full flex items-center justify-center text-xs text-white" style={{ background: 'var(--brand-teal)' }}>2</span>
                Additional Specifications
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Special Technical Requirements</label>
                  <textarea className="flex min-h-[80px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm" data-testid="enq-special" value={form.special_requirements} onChange={(e) => update('special_requirements', e.target.value)} placeholder="e.g., Explosion-proof, ATEX rated, specific compliance needs..." />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Additional Specifications</label>
                  <textarea className="flex min-h-[80px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm" data-testid="enq-additional" value={form.additional_specs} onChange={(e) => update('additional_specs', e.target.value)} placeholder="Any other details about your project..." />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Contact Details */}
          <Card className="mb-5" style={{ background: 'var(--surface-1)' }}>
            <CardContent className="p-5">
              <h3 className="font-semibold text-sm mb-4 flex items-center gap-2" style={{ fontFamily: 'Space Grotesk', color: 'var(--brand-navy)' }}>
                <span className="w-6 h-6 rounded-full flex items-center justify-center text-xs text-white" style={{ background: 'var(--brand-teal)' }}>3</span>
                Contact Details
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Company Name *</label>
                  <Input data-testid="enq-company" value={form.company_name} onChange={(e) => update('company_name', e.target.value)} required />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Contact Person Name *</label>
                  <Input data-testid="enq-contact" value={form.contact_name} onChange={(e) => update('contact_name', e.target.value)} required />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Phone Number</label>
                  <Input data-testid="enq-phone" value={form.phone} onChange={(e) => update('phone', e.target.value)} />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Email Address *</label>
                  <Input data-testid="enq-email" type="email" value={form.email} onChange={(e) => update('email', e.target.value)} required />
                </div>
              </div>
            </CardContent>
          </Card>

          <Button type="submit" data-testid="enq-submit" disabled={submitting} className="w-full text-white py-3 text-base" style={{ background: 'var(--brand-teal)' }}>
            {submitting ? 'Submitting...' : 'Submit Enquiry'}
          </Button>
        </form>
      </div>
    </section>
  );
}
